import { FC, useMemo } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { DataGrid } from "../datagrid";
import { TraderGrantDto, TraderGrantStatus } from "../../api/Api";

type Props = {
  rows: TraderGrantDto[];
  loading: boolean;
  onExtendExpiry: (row: TraderGrantDto) => void;
  onClearExpiry: (row: TraderGrantDto) => void;
  onRevoke: (row: TraderGrantDto) => void;
};

const statusColor: Record<
  TraderGrantStatus,
  "success" | "warning" | "default"
> = {
  Active: "success",
  Expiring: "warning",
  Expired: "default",
  Revoked: "default"
};

export const GrantsTable: FC<Props> = ({
  rows,
  loading,
  onExtendExpiry,
  onClearExpiry,
  onRevoke
}) => {
  const columns = useMemo<GridColDef<TraderGrantDto>[]>(
    () => [
      {
        field: "displayName",
        headerName: "Trader",
        minWidth: 350,
        renderCell: (params) => {
          const row = params.row;
          const isRevoked = row.status === "Revoked";
          return (
            <Box
              sx={{
                textDecoration: isRevoked ? "line-through" : "none",
                opacity: isRevoked ? 0.6 : 1,
                flex: 1,
                paddingX: "8px",
                justifyContent: "space-between",
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%"
              }}
            >
              <Typography variant="body2">{row.displayName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {row.email}
              </Typography>
            </Box>
          );
        }
      },
      {
        field: "department",
        headerName: "Company",
        width: 180,
        valueGetter: (_v, row) => row.department ?? "",
        renderCell: (params) =>
          params.row.department ? (
            params.row.department
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          )
      },
      {
        field: "grantedAt",
        headerName: "Granted at",
        width: 140,
        valueGetter: (_v, row) =>
          row.grantedAt ? new Date(row.grantedAt) : null,
        valueFormatter: (value: Date | null) =>
          value ? dayjs(value).format("ll") : ""
      },
      {
        field: "grantedBy",
        headerName: "Granted by",
        width: 220
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: (params) => {
          const status = params.row.status;
          if (!status) return null;
          return (
            <Chip
              size="small"
              label={status}
              color={statusColor[status]}
              variant={status === "Revoked" ? "outlined" : "filled"}
            />
          );
        }
      },
      {
        field: "expiresAt",
        headerName: "Expires",
        width: 140,
        valueGetter: (_v, row) =>
          row.expiresAt ? new Date(row.expiresAt) : null,
        renderCell: (params) =>
          params.row.expiresAt ? dayjs(params.row.expiresAt).format("ll") : "—"
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 360,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          if (row.status === "Revoked") return null;
          return (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              height="100%"
              justifyContent="flex-end"
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => onExtendExpiry(row)}
              >
                Change expiry
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!row.expiresAt}
                onClick={() => onClearExpiry(row)}
              >
                Clear expiry
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => onRevoke(row)}
              >
                Revoke
              </Button>
            </Stack>
          );
        }
      }
    ],
    [onExtendExpiry, onClearExpiry, onRevoke]
  );

  return (
    <DataGrid
      loading={loading}
      rows={rows}
      columns={columns}
      getRowId={(row) => `${row.entityId ?? ""}_${row.userObjectId ?? ""}`}
      disableRowSelectionOnClick
      hideFooterSelectedRowCount
    />
  );
};
